import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ImportPreviewGrid } from "@/components/catalog/ImportPreviewGrid";
import { useToast } from "@/contexts/ToastContext";
import { importsApi, ImportPreview, ImportPreviewRow, ImportReport } from "@/lib/api/imports";
import type { ItemType } from "@/lib/api/item-types";
import type { Location } from "@/lib/api/locations";
import type { Collection } from "@/lib/api/collections";
import type { BookCondition } from "@/lib/api/book-conditions";
import { useItemTypes } from "@/hooks/useItemTypes";
import { useLocations } from "@/hooks/useLocations";
import { useCollections } from "@/hooks/useCollections";
import { useBookConditions } from "@/hooks/useBookConditions";
import { formatNumberWithCommas } from "@/lib/formatters";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  Download,
  AlertCircle,
  FileText,
  X,
  ArrowRight,
  RotateCcw,
  Eye,
  Check,
  Loader2,
  Info,
  Package,
  Clock,
  XCircle,
  ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { validateBulkImportRows } from "@/lib/catalog/catalog-validate-form";

function csvEscapeCell(value: unknown): string {
  const s = value == null ? "" : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

type ImportStep = "upload" | "preview" | "review" | "importing" | "complete";

export function BulkImportPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step state
  const [currentStep, setCurrentStep] = useState<ImportStep>("upload");

  // File & defaults
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [defaults, setDefaults] = useState({
    defaultLocationId: "",
    defaultCollectionId: "",
    defaultConditionCode: "",
    defaultAcquisitionMode: "",
  });

  // Job state
  const [jobId, setJobId] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [previewRows, setPreviewRows] = useState<ImportPreviewRow[]>([]);
  const [report, setReport] = useState<ImportReport | null>(null);

  // Loading states
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reference data (cached via React Query)
  const { data: itemTypesRaw = [], isLoading: loadingTypes } = useItemTypes(true);
  const { data: locationsRaw = [], isLoading: loadingLocs } = useLocations(true);
  const { data: collectionsRaw = [], isLoading: loadingCols } = useCollections(true);
  const { data: conditionsRaw = [], isLoading: loadingConds } = useBookConditions(true);

  const itemTypes = itemTypesRaw.filter((t) => t.isActive);
  const locations = locationsRaw.filter((l) => l.isActive);
  const collections = collectionsRaw.filter((c) => c.isActive);
  const conditions = conditionsRaw.filter((c) => c.isActive).sort((a, b) => a.order - b.order);
  const loadingOptions = loadingTypes || loadingLocs || loadingCols || loadingConds;

  useEffect(() => {
    if (jobId && currentStep === "preview") {
      pollPreview();
    }
  }, [jobId, currentStep]);

  useEffect(() => {
    if (jobId && currentStep === "importing") {
      pollReport();
    }
  }, [jobId, currentStep]);

  const pollPreview = async () => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const previewData = await importsApi.getPreview(jobId);
        setPreview(previewData);
        setPreviewRows(previewData.rows);

        if (previewData.summary.total > 0) {
          clearInterval(interval);
          setProcessing(false);
        }
      } catch (error: any) {
        if (
          error.response?.status === 400 &&
          error.response?.data?.message?.includes("not yet generated")
        ) {
          return;
        }
        clearInterval(interval);
        setError(error.message);
        setProcessing(false);
      }
    }, 2000);

    setTimeout(() => clearInterval(interval), 5 * 60 * 1000);
  };

  const pollReport = async () => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const jobStatus = await importsApi.getJobStatus(jobId);

        if (jobStatus.status === "COMPLETED" || jobStatus.status === "FAILED") {
          try {
            const reportData = await importsApi.getReport(jobId);
            setReport(reportData);
          } catch {
            setReport({
              jobId,
              status: jobStatus.status,
              summary: jobStatus.summary || { rows: 0, success: 0, failed: 0 },
              reportData: null,
              errorMessage: jobStatus.errorMessage,
              createdAt: jobStatus.createdAt,
              updatedAt: jobStatus.updatedAt,
            });
          }
          setCurrentStep("complete");
          clearInterval(interval);
          setImporting(false);

          if (jobStatus.status === "COMPLETED") {
            toast.success("Import completed successfully!");
          } else {
            toast.error("Import failed. Check the report for details.");
          }
        }
      } catch (error: any) {
        if (error.response?.status === 400 || error.response?.status === 404) {
          return;
        }
        clearInterval(interval);
        setError(error.message);
        setImporting(false);
      }
    }, 3000);

    setTimeout(() => clearInterval(interval), 30 * 60 * 1000);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith(".csv")) {
        setFile(droppedFile);
      } else {
        toast.error("Please upload a CSV file.");
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Convert IDs to codes for the API
      const defaultsForApi = {
        defaultLocationCode: locations.find((l) => l.id === defaults.defaultLocationId)?.code || "",
        defaultCollectionCode:
          collections.find((c) => c.id === defaults.defaultCollectionId)?.code || "",
        defaultConditionCode: defaults.defaultConditionCode,
      };

      const result = await importsApi.upload(file, defaultsForApi);
      setJobId(result.jobId);
      setProcessing(true);
      setCurrentStep("preview");
      toast.success("File uploaded! Processing...");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to upload file");
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!jobId) return;

    const rowsToImport = previewRows.filter((r) => r.status === "FOUND");
    const validationIssues = validateBulkImportRows(rowsToImport, {
      defaultAcquisitionMode: defaults.defaultAcquisitionMode,
    });
    if (validationIssues.length > 0) {
      setError(validationIssues.slice(0, 5).join("; "));
      toast.error(
        validationIssues.length > 3
          ? `${validationIssues.length} rows missing required fields (subjects or acquisition mode)`
          : validationIssues.join("; ")
      );
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const approvedRows = previewRows.filter((r) => r.status === "FOUND").map((r) => r.rowIndex);
      await importsApi.confirm(jobId, approvedRows);
      setCurrentStep("importing");
      toast.success("Import started!");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to start import");
      toast.error("Failed to start import.");
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const BOM = "\uFEFF";
    const header = [
      "isbn",
      "copies",
      "itemTypeCode",
      "accessionNumber",
      "bindingType",
      "lcClassification",
      "callNumber",
      "locationCode",
      "collectionCode",
      "conditionCode",
      "statusCode",
      "subjects",
      "acquisitionMode",
      "publisher",
      "placeOfPublication",
      "publishedDate",
    ].join(",");
    const lines = [
      header,
      '9780134685991,3,TEXTBOOK,1001,hbk,QA76.73,QA76.73.J38 D67 2018,READERS-ROOM,GENERAL,GOOD,AVAILABLE,Computer science;Programming,PURCHASE,Pearson,"Boston, MA",2018',
      "9780596009205,2,REFERENCE,,pbk,QA76.73,QA76.73.P67 2020,STACKS,REFERENCE,NEW,AVAILABLE,Software engineering,PURCHASE,O'Reilly,Sebastopol CA,2020",
      "9781491950357,1,TEXTBOOK,,hbk,QA76.73,QA76.73.E45 2019,READERS-ROOM,GENERAL,FAIR,AVAILABLE,Data science,GIFT,Wiley,Hoboken NJ,2019",
    ];
    const csvContent = `${BOM}${lines.join("\n")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadReport = () => {
    if (!report?.reportData) return;

    const headers = [
      "Row",
      "Status",
      "ISBN",
      "Title",
      "Biblio ID",
      "Items created",
      "Barcodes",
      "Publisher",
      "Place of publication",
      "Published date",
      "Acquisition mode",
      "Error",
    ];
    const rows: string[] = [headers.map(csvEscapeCell).join(",")];

    if (report.reportData?.results) {
      for (const result of report.reportData.results as Array<{
        rowIndex?: number;
        success?: boolean;
        isbn?: string;
        title?: string;
        biblioId?: string;
        publisher?: string;
        placeOfPublication?: string;
        publishedDate?: string;
        acquisitionMode?: string;
        items?: Array<{ barcode: string }>;
        error?: string;
      }>) {
        const row = [
          result.rowIndex ?? "",
          result.success ? "Success" : "Failed",
          result.isbn ?? "",
          result.title ?? "",
          result.biblioId ?? "",
          result.items?.length ?? 0,
          result.items?.map((i) => i.barcode).join("; ") ?? "",
          result.publisher ?? "",
          result.placeOfPublication ?? "",
          result.publishedDate ?? "",
          result.acquisitionMode ?? "",
          result.error ?? "",
        ];
        rows.push(row.map(csvEscapeCell).join(","));
      }
    }

    const csv = `\uFEFF${rows.join("\n")}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `import-report-${jobId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleStartOver = () => {
    setFile(null);
    setJobId(null);
    setPreview(null);
    setPreviewRows([]);
    setReport(null);
    setError(null);
    setCurrentStep("upload");
  };

  // Step configuration
  const steps = [
    { id: "upload", label: "Upload CSV", icon: Upload },
    { id: "preview", label: "Preview Data", icon: Eye },
    { id: "review", label: "Review & Edit", icon: FileText },
    { id: "importing", label: "Importing", icon: Loader2 },
    { id: "complete", label: "Complete", icon: Check },
  ];

  const getCurrentStepIndex = () => steps.findIndex((s) => s.id === currentStep);

  if (loadingOptions) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <div className="mx-auto max-w-7xl">
          <PageHeader
            title="Bulk ISBN Import"
            description="Import multiple books at once using a CSV file"
            breadcrumbItems={[{ label: "Catalog" }, { label: "Bulk Import" }]}
            showSearch={false}
          />
          <div className="flex items-center justify-center py-24">
            <LoadingSpinner size="xl" label="Loading import tools..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="mx-auto max-w-7xl">
        <PageHeader
          title="Bulk ISBN Import"
          description="Import multiple books at once using a CSV file with ISBN auto-cataloging"
          breadcrumbItems={[{ label: "Catalog" }, { label: "Bulk Import" }]}
          showSearch={false}
        />

        {/* Step Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = step.id === currentStep;
              const isComplete = getCurrentStepIndex() > index;
              const isImporting = step.id === "importing" && currentStep === "importing";

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                        isComplete && "bg-green-500 text-white",
                        isActive && !isImporting && "bg-primary text-white shadow-lg",
                        isImporting && "bg-amber-500 text-white",
                        !isComplete && !isActive && "bg-gray-200 text-gray-500"
                      )}
                    >
                      {isComplete ? (
                        <Check className="w-5 h-5" />
                      ) : isImporting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <StepIcon className="w-4 h-4" />
                      )}
                    </div>
                    <span
                      className={cn(
                        "mt-2 text-xs font-medium",
                        isActive ? "text-primary" : "text-gray-500"
                      )}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        "w-16 h-0.5 mx-1 rounded transition-colors",
                        isComplete ? "bg-green-500" : "bg-gray-200"
                      )}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <Card className="p-4 mb-6 bg-red-50 border-red-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
              <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* STEP 1: Upload */}
        {currentStep === "upload" && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Card className="overflow-hidden bg-white shadow-sm">
                <div className="p-6">
                  <h2 className="flex items-center gap-2 mb-6 text-xl font-semibold text-gray-900">
                    <FileSpreadsheet className="w-6 h-6 text-primary" />
                    Upload Your CSV File
                  </h2>

                  {/* Drop Zone */}
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                      "relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
                      dragActive
                        ? "border-primary bg-primary/5"
                        : file
                          ? "border-green-500 bg-green-50"
                          : "border-gray-300 hover:border-gray-400"
                    )}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    {file ? (
                      <div className="flex flex-col items-center">
                        <div className="flex items-center justify-center w-16 h-16 mb-4 bg-green-100 rounded-full">
                          <FileText className="w-8 h-8 text-green-600" />
                        </div>
                        <p className="text-lg font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFile(null);
                          }}
                          className="mt-3 text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <div className="flex items-center justify-center w-16 h-16 mb-4 bg-gray-100 rounded-full">
                          <Upload className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-lg font-medium text-gray-900">Drop your CSV file here</p>
                        <p className="mt-1 text-sm text-gray-500">
                          or click to browse your computer
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Default Values */}
                  <div className="mt-6">
                    <h3 className="mb-4 text-sm font-medium text-gray-700">
                      Default Values (Optional)
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <Label className="text-xs text-gray-500">Default Location</Label>
                        <Select
                          value={defaults.defaultLocationId || "none"}
                          onValueChange={(value) =>
                            setDefaults({
                              ...defaults,
                              defaultLocationId: value === "none" ? "" : value,
                            })
                          }
                        >
                          <SelectTrigger className="mt-1.5">
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {locations.map((loc) => (
                              <SelectItem key={loc.id} value={loc.id}>
                                {loc.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Default Collection</Label>
                        <Select
                          value={defaults.defaultCollectionId || "none"}
                          onValueChange={(value) =>
                            setDefaults({
                              ...defaults,
                              defaultCollectionId: value === "none" ? "" : value,
                            })
                          }
                        >
                          <SelectTrigger className="mt-1.5">
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {collections.map((col) => (
                              <SelectItem key={col.id} value={col.id}>
                                {col.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Default Condition</Label>
                        <Select
                          value={defaults.defaultConditionCode || "none"}
                          onValueChange={(value) =>
                            setDefaults({
                              ...defaults,
                              defaultConditionCode: value === "none" ? "" : value,
                            })
                          }
                        >
                          <SelectTrigger className="mt-1.5">
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {conditions.map((cond) => (
                              <SelectItem key={cond.code} value={cond.code}>
                                {cond.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <RequiredLabel className="text-xs text-gray-500" required>
                          Default Acquisition Mode
                        </RequiredLabel>
                        <Input
                          value={defaults.defaultAcquisitionMode}
                          onChange={(e) =>
                            setDefaults({
                              ...defaults,
                              defaultAcquisitionMode: e.target.value,
                            })
                          }
                          placeholder="e.g., PURCHASE, GIFT, EXCHANGE"
                          className="mt-1.5"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Upload Button */}
                  <Button
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    size="lg"
                    className="w-full gap-2 mt-6"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Upload & Process
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <Card className="p-4 bg-white shadow-sm">
                <h3 className="flex items-center gap-2 mb-3 font-semibold text-gray-900">
                  <Download className="w-4 h-4 text-primary" />
                  CSV Template
                </h3>
                <p className="mb-4 text-sm text-gray-600">
                  Required columns: <span className="font-medium">isbn</span>,{" "}
                  <span className="font-medium">copies</span>. Other columns are optional (codes
                  must match your library’s item types, locations, collections, conditions, and
                  statuses). <span className="font-medium">acquisitionMode</span> values: PURCHASE,
                  DONATION, GIFT, EXCHANGE, TETFUND.
                </p>
                <Button variant="outline" onClick={handleDownloadTemplate} className="w-full gap-2">
                  <Download className="w-4 h-4" />
                  Download Template
                </Button>
              </Card>

              <Card className="p-4 bg-white shadow-sm">
                <h3 className="flex items-center gap-2 mb-3 font-semibold text-gray-900">
                  <Info className="w-4 h-4 text-gray-400" />
                  CSV Format
                </h3>
                <div className="space-y-2 text-xs text-gray-600">
                  <p>
                    <strong>Required:</strong> isbn
                  </p>
                  <p>
                    <strong>Optional:</strong> copies, itemTypeCode, accessionNumber, bindingType,
                    lcClassification, callNumber, locationCode, collectionCode, conditionCode,
                    statusCode
                  </p>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* STEP 2: Preview */}
        {currentStep === "preview" && (
          <Card className="overflow-hidden bg-white shadow-sm">
            <div className="p-6">
              <h2 className="flex items-center gap-2 mb-6 text-xl font-semibold text-gray-900">
                <Eye className="w-6 h-6 text-primary" />
                Preview Import Data
              </h2>

              {processing && !preview ? (
                <div className="py-16 text-center">
                  <LoadingSpinner size="xl" label="Processing CSV and fetching metadata..." />
                  <p className="mt-4 text-sm text-gray-500">
                    This may take a few moments depending on the file size.
                  </p>
                </div>
              ) : preview ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-4 mb-6 md:grid-cols-5">
                    <Card className="p-4 text-center border border-gray-200">
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumberWithCommas(preview.summary.total)}
                      </p>
                      <p className="text-xs text-gray-500">Total Rows</p>
                    </Card>
                    <Card className="p-4 text-center border border-green-200 bg-green-50">
                      <p className="text-2xl font-bold text-green-600">
                        {formatNumberWithCommas(preview.summary.found)}
                      </p>
                      <p className="text-xs text-green-600">Found</p>
                    </Card>
                    <Card className="p-4 text-center border border-red-200 bg-red-50">
                      <p className="text-2xl font-bold text-red-600">
                        {formatNumberWithCommas(preview.summary.notFound)}
                      </p>
                      <p className="text-xs text-red-600">Not Found</p>
                    </Card>
                    <Card className="p-4 text-center border border-amber-200 bg-amber-50">
                      <p className="text-2xl font-bold text-amber-600">
                        {formatNumberWithCommas(preview.summary.needsReview)}
                      </p>
                      <p className="text-xs text-amber-600">Needs Review</p>
                    </Card>
                    <Card className="p-4 text-center border border-gray-200">
                      <p className="text-2xl font-bold text-gray-600">
                        {formatNumberWithCommas(preview.summary.errors)}
                      </p>
                      <p className="text-xs text-gray-500">Errors</p>
                    </Card>
                  </div>

                  {/* Preview Grid */}
                  <ImportPreviewGrid
                    rows={previewRows}
                    onRowsChange={setPreviewRows}
                    itemTypes={itemTypes}
                    locations={locations.map((loc) => ({ ...loc, code: loc.code ?? undefined }))}
                    collections={collections.map((coll) => ({
                      ...coll,
                      code: coll.code ?? undefined,
                    }))}
                    conditions={conditions}
                  />

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
                    <Button variant="outline" onClick={handleStartOver}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Start Over
                    </Button>
                    <Button onClick={() => setCurrentStep("review")} className="gap-2">
                      Continue to Review
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          </Card>
        )}

        {/* STEP 3: Review & Confirm */}
        {currentStep === "review" && preview && (
          <Card className="overflow-hidden bg-white shadow-sm">
            <div className="p-6">
              <h2 className="flex items-center gap-2 mb-6 text-xl font-semibold text-gray-900">
                <FileText className="w-6 h-6 text-primary" />
                Review & Confirm Import
              </h2>

              {/* Summary */}
              <div className="p-4 mb-6 border border-gray-200 rounded-lg bg-gray-50">
                <h3 className="mb-2 font-medium text-gray-900">Import Summary</h3>
                <p className="text-sm text-gray-600">
                  <strong>{previewRows.filter((r) => r.status === "FOUND").length}</strong> books
                  will be imported. Books marked as "Not Found" or with errors will be skipped.
                </p>
              </div>

              {/* Preview Grid (read-only mode could be added) */}
              <ImportPreviewGrid
                rows={previewRows}
                onRowsChange={setPreviewRows}
                itemTypes={itemTypes}
                locations={locations.map((loc) => ({ ...loc, code: loc.code ?? undefined }))}
                collections={collections.map((coll) => ({
                  ...coll,
                  code: coll.code ?? undefined,
                }))}
                conditions={conditions}
              />

              {/* Actions */}
              <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
                <Button variant="outline" onClick={() => setCurrentStep("preview")}>
                  Back
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  disabled={
                    importing || previewRows.filter((r) => r.status === "FOUND").length === 0
                  }
                  className="gap-2"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Confirm & Import
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* STEP 4: Importing */}
        {currentStep === "importing" && (
          <Card className="overflow-hidden bg-white shadow-sm">
            <div className="p-12 text-center">
              <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full">
                <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Import in Progress</h2>
              <p className="max-w-md mx-auto text-gray-500">
                We're adding your books to the catalog. This may take a few minutes for large
                imports.
              </p>
              <div className="flex items-center justify-center gap-2 mt-6 text-sm text-gray-400">
                <Clock className="w-4 h-4" />
                Please don't close this page
              </div>
            </div>
          </Card>
        )}

        {/* STEP 5: Complete */}
        {currentStep === "complete" && report && (
          <Card className="overflow-hidden bg-white shadow-sm">
            <div className="p-8">
              <div className="text-center">
                <div
                  className={cn(
                    "flex items-center justify-center w-20 h-20 mx-auto mb-6 rounded-full",
                    report.status === "COMPLETED" ? "bg-green-100" : "bg-red-100"
                  )}
                >
                  {report.status === "COMPLETED" ? (
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  ) : (
                    <XCircle className="w-10 h-10 text-red-600" />
                  )}
                </div>
                <h2 className="mb-2 text-2xl font-bold text-gray-900">
                  {report.status === "COMPLETED" ? "Import Complete!" : "Import Failed"}
                </h2>
                <p className="text-gray-500">
                  {report.status === "COMPLETED"
                    ? "Your books have been added to the catalog."
                    : report.errorMessage || "An error occurred during import."}
                </p>
              </div>

              {/* Report Summary */}
              <div className="grid grid-cols-3 gap-4 my-8">
                <Card className="p-4 text-center border border-gray-200">
                  <p className="text-2xl font-bold text-gray-900">{report.summary.rows}</p>
                  <p className="text-xs text-gray-500">Total Processed</p>
                </Card>
                <Card className="p-4 text-center border border-green-200 bg-green-50">
                  <p className="text-2xl font-bold text-green-600">{report.summary.success}</p>
                  <p className="text-xs text-green-600">Successful</p>
                </Card>
                <Card className="p-4 text-center border border-red-200 bg-red-50">
                  <p className="text-2xl font-bold text-red-600">{report.summary.failed}</p>
                  <p className="text-xs text-red-600">Failed</p>
                </Card>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3 max-w-md mx-auto">
                <Button onClick={handleStartOver} size="lg" className="gap-2">
                  <RotateCcw className="w-5 h-5" />
                  Start New Import
                </Button>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleDownloadReport}
                    className="flex-1 gap-2"
                    disabled={!report.reportData}
                  >
                    <Download className="w-4 h-4" />
                    Download Report
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/catalog")}
                    className="flex-1 gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Catalog
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
